import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, HardHat } from "lucide-react";

/**
 * Shared shell for the Privacy Policy and Terms of Service pages.
 * Publicly accessible (no auth) so Apple App Review can reach them.
 */
export default function LegalPage({ title, effectiveDate, sections }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Slim public nav */}
      <header className="border-b-2 border-foreground dark:border-safety-yellow">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-9 w-9 bg-safety-yellow text-black border-2 border-black dark:border-safety-yellow">
              <HardHat className="w-5 h-5" strokeWidth={3} />
            </span>
            <span className="font-heading text-xl">KARM<span className="text-safety-yellow">.</span>SAFETY</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:text-safety-yellow">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        <div className="inline-flex items-center gap-2 border-2 border-safety-yellow bg-safety-yellow/15 px-3 py-1 mb-3 text-[10px] font-bold uppercase tracking-[0.25em]">
          Legal
        </div>
        <h1 className="font-heading text-4xl md:text-5xl leading-none">{title}</h1>
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-3">
          Effective date · {effectiveDate}
        </div>

        <div className="border-2 border-safety-yellow bg-safety-yellow/10 p-4 mt-6 text-sm leading-relaxed">
          <b>Notice.</b> This document was generated as a starting point and should be reviewed
          by qualified legal counsel before any commercial deployment. It is provided in good faith
          but does not constitute legal advice.
        </div>

        <article className="mt-8 space-y-8">
          {sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-heading text-2xl mb-2">{s.heading.toUpperCase()}</h2>
              {s.body.map((p, i) =>
                typeof p === "string"
                  ? <p key={i} className="text-sm leading-relaxed mb-3 text-foreground/90">{p}</p>
                  : (
                    <ul key={i} className="list-disc pl-5 space-y-1 text-sm text-foreground/90 mb-3">
                      {p.items.map((li, j) => <li key={j}>{li}</li>)}
                    </ul>
                  )
              )}
            </section>
          ))}
        </article>

        <footer className="mt-16 pt-8 border-t-2 border-foreground/20 dark:border-safety-yellow/30 text-xs text-muted-foreground">
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-safety-yellow font-bold uppercase tracking-wider">Privacy</Link>
            <Link to="/terms" className="hover:text-safety-yellow font-bold uppercase tracking-wider">Terms</Link>
            <a href="mailto:shane@karmsafety.com" className="hover:text-safety-yellow font-bold uppercase tracking-wider">Contact</a>
          </div>
          <div className="mt-4">© {new Date().getFullYear()} KARM LLC. All rights reserved.</div>
        </footer>
      </main>
    </div>
  );
}
